import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import type { Graph } from '../model';
import type { Layout } from './geometry';

/**
 * Autosave of the journey editor (docs/pilot/SPEC.md §12.5). 1.5 s after the last change (and when the tab is hidden):
 * a changed graph → PUT …/editor-draft (shared, by rev); only moved nodes of the saved version → PUT
 * …/versions/:v/layout (no new version, no new approval); the graph back to the saved one → the draft is deleted.
 * Without a connection the state is kept in localStorage (`mo.journey-draft.<id>`) and retried on the next change.
 */

export const AUTOSAVE_MS = 1500;
export const backupKey = (journeyId: string) => `mo.journey-draft.${journeyId}`;

export interface DraftBackup {
  graph: Graph;
  layout: Layout;
  base_version: number;
  saved_at: string;
}

export function readBackup(journeyId: string): DraftBackup | null {
  try {
    const raw = window.localStorage.getItem(backupKey(journeyId));
    if (!raw) return null;
    const b = JSON.parse(raw) as DraftBackup;
    return b && typeof b === 'object' && b.graph && typeof b.graph === 'object' ? b : null;
  } catch {
    return null;
  }
}
export function writeBackup(journeyId: string, b: DraftBackup): void {
  try {
    window.localStorage.setItem(backupKey(journeyId), JSON.stringify(b));
  } catch {
    /* private mode or full storage: the server draft is the copy that matters */
  }
}
export function clearBackup(journeyId: string): void {
  try {
    window.localStorage.removeItem(backupKey(journeyId));
  } catch {
    /* ignore */
  }
}

export type SaveStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'saving' }
  | { kind: 'saved'; at: string; what: 'draft' | 'layout' | 'cleared' }
  | { kind: 'offline' }
  | { kind: 'error'; text: string }
  | { kind: 'conflict'; rev: number; by: string | null; at: string | null };

export interface AutosaveInput {
  journeyId: string;
  enabled: boolean;
  graph: Graph | null;
  layout: Layout | null;
  graphDirty: boolean;
  layoutDirty: boolean;
  baseVersion: number;
  /** Positions of the saved version were written: the page moves its «saved layout» mark. */
  onLayoutSaved: (layout: Layout) => void;
}

export function useAutosave(o: AutosaveInput) {
  const [status, setStatus] = useState<SaveStatus>({ kind: 'idle' });
  /** The draft rev this tab knows (0 = it saw no draft). */
  const rev = useRef(0);
  const timer = useRef<number | null>(null);
  const inflight = useRef<Promise<void> | null>(null);
  const latest = useRef(o);
  latest.current = o;
  const blocked = useRef(false);

  const run = useCallback(async () => {
    timer.current = null;
    const s = latest.current;
    if (!s.enabled || !s.graph || !s.layout || blocked.current) return;
    if (inflight.current) await inflight.current;
    const job = (async () => {
      try {
        if (s.graphDirty) {
          writeBackup(s.journeyId, { graph: s.graph!, layout: s.layout!, base_version: s.baseVersion, saved_at: new Date().toISOString() });
          setStatus({ kind: 'saving' });
          const r = await api.put<{ rev: number; saved_at: string }>(`/journeys/${s.journeyId}/editor-draft`, { graph: s.graph, layout: s.layout, base_version: s.baseVersion, rev: rev.current });
          rev.current = r.rev;
          clearBackup(s.journeyId);
          setStatus({ kind: 'saved', at: r.saved_at, what: 'draft' });
        } else if (s.layoutDirty && s.baseVersion > 0) {
          setStatus({ kind: 'saving' });
          await api.put(`/journeys/${s.journeyId}/versions/${s.baseVersion}/layout`, { layout: s.layout });
          s.onLayoutSaved(s.layout!);
          if (rev.current > 0) {
            await api.del(`/journeys/${s.journeyId}/editor-draft`).catch(() => undefined);
            rev.current = 0;
          }
          clearBackup(s.journeyId);
          setStatus({ kind: 'saved', at: new Date().toISOString(), what: 'layout' });
        } else if (rev.current > 0) {
          // Undone back to the saved version: nothing to keep.
          await api.del(`/journeys/${s.journeyId}/editor-draft`);
          rev.current = 0;
          clearBackup(s.journeyId);
          setStatus({ kind: 'saved', at: new Date().toISOString(), what: 'cleared' });
        }
      } catch (err) {
        if (err instanceof ApiError && err.code === 'JOURNEY_DRAFT_CONFLICT') {
          const d = err.details as unknown as { rev: number; updated_by: { name: string | null } | null; updated_at: string | null };
          blocked.current = true;
          setStatus({ kind: 'conflict', rev: d.rev, by: d.updated_by?.name ?? null, at: d.updated_at });
        } else if (err instanceof ApiError) {
          setStatus({ kind: 'error', text: err.message });
        } else {
          setStatus({ kind: 'offline' });
        }
      }
    })();
    inflight.current = job;
    await job;
    inflight.current = null;
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => void run(), AUTOSAVE_MS);
  }, [run]);

  // Every change of the state restarts the 1.5 s timer.
  useEffect(() => {
    if (!o.enabled || !o.graph) return;
    if (!o.graphDirty && !o.layoutDirty && rev.current === 0) return;
    setStatus((st) => (st.kind === 'conflict' ? st : { kind: 'pending' }));
    schedule();
  }, [o.graph, o.layout, o.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving the tab saves at once; coming back online retries.
  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden' && timer.current) {
        window.clearTimeout(timer.current);
        void run();
      }
    };
    const online = () => latest.current.graphDirty && schedule();
    document.addEventListener('visibilitychange', flush);
    window.addEventListener('online', online);
    return () => {
      document.removeEventListener('visibilitychange', flush);
      window.removeEventListener('online', online);
    };
  }, [run, schedule]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  /** Before «Сохранить версию»: no autosave may land after it. Returns the rev to send as `draft_rev`. */
  const cancel = useCallback(async () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    if (inflight.current) await inflight.current;
    return rev.current;
  }, []);

  /** «Перезаписать моей» after a conflict. */
  const overwrite = useCallback(async (theirRev: number) => {
    const s = latest.current;
    if (!s.graph || !s.layout) return;
    setStatus({ kind: 'saving' });
    try {
      const r = await api.put<{ rev: number; saved_at: string }>(`/journeys/${s.journeyId}/editor-draft`, { graph: s.graph, layout: s.layout, base_version: s.baseVersion, rev: theirRev, force: true });
      rev.current = r.rev;
      blocked.current = false;
      clearBackup(s.journeyId);
      setStatus({ kind: 'saved', at: r.saved_at, what: 'draft' });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'JOURNEY_DRAFT_CONFLICT') {
        const d = err.details as unknown as { rev: number; updated_by: { name: string | null } | null; updated_at: string | null };
        setStatus({ kind: 'conflict', rev: d.rev, by: d.updated_by?.name ?? null, at: d.updated_at });
      } else setStatus({ kind: 'error', text: (err as Error).message });
    }
  }, []);

  /** The page took the server draft (rev) or dropped it (0). */
  const adopt = useCallback((r: number) => {
    rev.current = r;
    blocked.current = false;
    setStatus({ kind: 'idle' });
  }, []);

  return { status, cancel, overwrite, adopt, rev };
}
